import { Inject, Injectable } from '@angular/core';
import { ReviewerDetails } from '@app/app/page-modules/playlist/models';
import { GOALS_DATA_SERVICE, GoalsDataService } from '@app/app/page-modules/playlist/services/create/goals/goals-data.service';
import { Program } from '@app/app/page-modules/programs/models/program.model';
import { ContentHelper } from '@app/app/shared/helpers/content-helper';
import { notDevelopmentDomains } from '@app/app/shared/helpers/development-domains.helper';
import { BadgeResponse, GoalResponse } from '@app/app/shared/models';
import { BooleanFilter } from '@app/app/shared/models/admin/boolean-filters.model';
import { Certificate, CertificateTemplate, UserCertificateDetails } from '@app/app/shared/models/certificate/models/certificate.model';
import { GoalBadgeIssuanceStatus } from '@app/app/shared/models/playlist/goals.model';
import { CERTIFICATE_DATA_SERVICE, CertificateDataService } from '@app/app/shared/services/certificate/certificate-data.service';
import { LoadableState, dataLoadedState, defaultLoadableState, errorState, loadingState } from '@app/app/shared/store';
import { UserAuthState, UserAuthStateModel } from '@app/app/user-auth/store/user-auth.state';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { CORE_PROGRAM_DATA_SERVICE, ProgramDataService } from '../../services/create/core/data.service';
import {
  ClearProgramView,
  LoadProgramCertificate,
  LoadProgramDetails,
  LoadProgramGoal,
  SaveProgramParticipantFilters,
  SetProgramSkills,
  SetProgramViewDetails,
} from './program-view.actions';

export interface ProgramViewStateModel {
  program: LoadableState<Program>;
  participantFilters: BooleanFilter[];
  goal: GoalResponse | null;
  goalBadge: BadgeResponse | null;
  badgeIssuanceStatus: GoalBadgeIssuanceStatus | null;
  certificate: Certificate | null;
  issuedCertificate: UserCertificateDetails | null;
  certificateTemplate: CertificateTemplate | null;
}

@State<ProgramViewStateModel>({
  name: 'programView',
  defaults: {
    program: defaultLoadableState(),
    participantFilters: [],
    goal: null,
    goalBadge: null,
    badgeIssuanceStatus: null,
    certificate: null,
    issuedCertificate: null,
    certificateTemplate: null,
  },
})
@Injectable()
export class ProgramViewState {
  constructor(
    @Inject(CORE_PROGRAM_DATA_SERVICE) private programDataService: ProgramDataService,
    @Inject(GOALS_DATA_SERVICE) private goalsDataService: GoalsDataService,
    @Inject(CERTIFICATE_DATA_SERVICE) private certificateDataService: CertificateDataService,
    private store: Store,
  ) {}

  @Selector()
  static programState({ program }: ProgramViewStateModel): LoadableState<Program> {
    return program;
  }

  @Selector()
  static programId({ program }: ProgramViewStateModel): string | undefined {
    return program.data?._id;
  }

  @Selector()
  static programDefaultReviewers({ program }: ProgramViewStateModel): ReviewerDetails[] {
    const collaborators = program.data?.adminRights ?? [];
    const reviewers = new Map(
      collaborators.map((collaborator) => [
        collaborator.uid,
        {
          uid: collaborator.uid,
          firstName: collaborator.firstName,
          lastName: collaborator.lastName,
          email: collaborator.email,
          imageUrl: collaborator.imageUrl,
          isAdmin: true,
          rejected: false,
        },
      ]),
    );
    return [...reviewers.values()];
  }

  @Selector([UserAuthState])
  static canOpenProgramPages({ program }: ProgramViewStateModel, userAuthState: UserAuthStateModel): boolean | undefined {
    if (!program.data?.permission) {
      return undefined;
    } else {
      const permission = program.data.permission;
      return (
        permission.canEdit ||
        permission.canEditBadge ||
        permission.canEditGoal ||
        permission.canPublish ||
        permission.canViewAnalytics ||
        permission.canManageCollaboration ||
        permission.canManageTargeting ||
        permission.reviewerDashboardPermission.accessForGroups.length > 0
      );
    }
  }

  @Selector([UserAuthState])
  static canClonePlaylist({ program }: ProgramViewStateModel, userAuthState: UserAuthStateModel): boolean | undefined {
    if ((ContentHelper.isFrameMode() && notDevelopmentDomains(userAuthState?.orgDetails?.data?.domain)) || program.data?.syndicated) {
      return false;
    }
    if (!program.data?.permission) {
      return undefined;
    } else {
      const permission = program.data.permission;
      return permission.canClone;
    }
  }

  @Selector([UserAuthState])
  static canOpenEditSettings({ program }: ProgramViewStateModel, userAuthState: UserAuthStateModel): boolean | undefined {
    if (!program.data?.permission || program.data.syndicated) {
      return undefined;
    } else {
      const permission = program.data.permission;
      return (
        permission.canEdit ||
        permission.canEditBadge ||
        permission.canEditGoal ||
        permission.canPublish ||
        permission.canManageCollaboration ||
        permission.canManageTargeting
      );
    }
  }

  @Selector([UserAuthState])
  static canEditGoal({ program }: ProgramViewStateModel, userAuthState: UserAuthStateModel): boolean | undefined {
    if (ContentHelper.isFrameMode() && notDevelopmentDomains(userAuthState?.orgDetails?.data?.domain)) {
      return false;
    }
    return !!program && !!program.data ? program.data.permission.canEditGoal : undefined;
  }

  @Selector([UserAuthState])
  static canPublish(programViewStateModel: ProgramViewStateModel): boolean | undefined {
    const resource = programViewStateModel.program;
    return !!resource && !!resource.data ? resource.data.permission.canPublish : undefined;
  }

  @Selector([UserAuthState])
  static canViewMembers(programViewStateModel: ProgramViewStateModel, userAuthState: UserAuthStateModel): boolean | undefined {
    const resource = programViewStateModel.program;
    return !!resource && !!resource.data ? resource.data.permission.canViewAnalytics : undefined;
  }

  @Selector([UserAuthState])
  static canViewAnalytics(programViewStateModel: ProgramViewStateModel, userAuthState: UserAuthStateModel): boolean | undefined {
    const resource = programViewStateModel.program;
    return !!resource && !!resource.data ? resource.data.permission.canViewAnalytics : undefined;
  }

  @Selector([UserAuthState])
  static canViewReviewerDashboard(programViewStateModel: ProgramViewStateModel, userAuthState: UserAuthStateModel): boolean | undefined {
    if (ContentHelper.isFrameMode() && notDevelopmentDomains(userAuthState?.orgDetails?.data?.domain)) {
      return false;
    }
    const resource = programViewStateModel.program;
    return !!resource && !!resource.data ? resource.data.permission?.reviewerDashboardPermission?.accessForGroups?.length > 0 : undefined;
  }

  @Selector()
  static getParticipantFilters(state: ProgramViewStateModel): BooleanFilter[] {
    return state.participantFilters;
  }

  @Selector()
  static programGoal({ goal }: ProgramViewStateModel): GoalResponse | null {
    return goal;
  }

  @Selector()
  static programGoalBadge({ goalBadge }: ProgramViewStateModel): BadgeResponse | null {
    return goalBadge;
  }

  @Selector()
  static programBadgeIssuanceStatus({ badgeIssuanceStatus }: ProgramViewStateModel): GoalBadgeIssuanceStatus | null {
    return badgeIssuanceStatus;
  }

  @Selector()
  static programCertificate({ certificate }: ProgramViewStateModel): Certificate | null {
    return certificate;
  }

  @Selector()
  static programIssuedCertificate({ issuedCertificate }: ProgramViewStateModel): UserCertificateDetails | null {
    return issuedCertificate;
  }

  @Selector()
  static programCertificateTemplate({ certificateTemplate }: ProgramViewStateModel): CertificateTemplate | null {
    return certificateTemplate;
  }

  @Action(SetProgramViewDetails)
  setPlaylistViewDetails({ patchState }: StateContext<ProgramViewStateModel>, { program }: SetProgramViewDetails) {
    patchState({
      program: dataLoadedState(program),
    });
  }

  @Action(LoadProgramDetails)
  loadResourceDetails(
    { getState, patchState }: StateContext<ProgramViewStateModel>,
    { programUri, languageCode, playlistPath, isCollection }: LoadProgramDetails,
  ) {
    const loadedProgram = getState().program?.data;
    const resolvedIsCollection = isCollection ?? (loadedProgram?.uri === programUri && loadedProgram?.type === 'COLLECTION');

    patchState({
      program: loadingState(),
      participantFilters: [],
    });

    const detail$ = resolvedIsCollection
      ? this.programDataService.getCollectionByUri(programUri, playlistPath, languageCode)
      : this.programDataService.getProgram(programUri, languageCode, playlistPath);

    return detail$.pipe(
      tap((program) => {
        patchState({
          program: dataLoadedState(program.value),
        });
        this.store.dispatch(new LoadProgramGoal(program.value._id, languageCode));
        this.store.dispatch(new LoadProgramCertificate(program.value._id));
      }),
      catchError((error) => {
        patchState({
          program: errorState(error.message),
        });
        return of(error);
      }),
    );
  }

  @Action(LoadProgramGoal, { cancelUncompleted: true })
  loadProgramGoal({ patchState }: StateContext<ProgramViewStateModel>, { programId, languageCode }: LoadProgramGoal) {
    patchState({ goal: null, goalBadge: null, badgeIssuanceStatus: null });
    return this.goalsDataService.getGoal(programId, languageCode).pipe(
      switchMap(({ value }) => {
        patchState({ goal: value ?? null });
        if (value?.connectedBadge?.badgeId) {
          return forkJoin({
            badge: this.goalsDataService.getBadge(value.connectedBadge.badgeId, languageCode),
            issuance: this.goalsDataService.getGoalBadgeIssuanceStatus(programId),
          }).pipe(
            tap(({ badge, issuance }) => {
              patchState({
                goalBadge: badge.isSuccess ? badge.value : null,
                badgeIssuanceStatus: issuance,
              });
            }),
          );
        }
        patchState({ goalBadge: null, badgeIssuanceStatus: null });
        return of(null);
      }),
    );
  }

  @Action(LoadProgramCertificate, { cancelUncompleted: true })
  loadProgramCertificate({ patchState }: StateContext<ProgramViewStateModel>, { programId }: LoadProgramCertificate) {
    patchState({ certificate: null, issuedCertificate: null, certificateTemplate: null });
    return this.certificateDataService.getCertificate(programId).pipe(
      switchMap(({ value: certificate }) => {
        patchState({ certificate: certificate ?? null });
        if (certificate?.status !== 'ACTIVE') {
          return of(null);
        }
        return forkJoin({
          issued: this.certificateDataService.getIssuedCertificates(),
          templates: this.certificateDataService.getTemplates(),
        }).pipe(
          tap(({ issued, templates }) => {
            patchState({
              issuedCertificate: (issued.value ?? []).find((issuedCert) => issuedCert.resourceId === programId) ?? null,
              certificateTemplate: (templates.value ?? []).find((template) => template.uid === certificate.templateId) ?? null,
            });
          }),
        );
      }),
    );
  }

  @Action(ClearProgramView)
  clearProgramView(ctx: StateContext<ProgramViewStateModel>) {
    ctx.setState({
      program: defaultLoadableState(),
      participantFilters: [],
      goal: null,
      goalBadge: null,
      badgeIssuanceStatus: null,
      certificate: null,
      issuedCertificate: null,
      certificateTemplate: null,
    });
  }

  @Action(SaveProgramParticipantFilters)
  saveProgramParticipantFilters({ patchState }: StateContext<ProgramViewStateModel>, { filters }: SaveProgramParticipantFilters) {
    patchState({
      participantFilters: filters,
    });
  }

  @Action(SetProgramSkills)
  setProgramSkills({ getState, patchState }: StateContext<ProgramViewStateModel>, { skills }: SetProgramSkills) {
    const current = getState().program;
    if (!current.data) {
      return;
    }
    patchState({
      program: dataLoadedState({ ...current.data, skills }),
    });
  }
}
